import React, { useEffect, useState, useContext, useRef } from "react";
import DatePicker from "react-datepicker";
import { FaChevronDown } from "react-icons/fa";
import socket from "../../socket";
import { ProjectContext } from "../../App";
import api from "../../api/axiosConfig";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { useAuth } from "../Authentication/AuthContext";
import InvoiceScope from "./InvoiceScope";

const fetchData = async (url, setData, setError) => {
  try {
    const response = await api.get(url); // Axios automatically adds the baseURL and headers
    setData(response.data); // Axios automatically parses JSON
  } catch (error) {
    console.error("Error fetching data:", error);
    setError(error); // Handle error
  }
};

const Invoices = ({
  showInvoiceTable,
  setShowInvoiceTable,
  projectId,
  phaseId,
}) => {
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceScope, setInvoiceScope] = useState([]);
  const [selectedScope, setSelectedScope] = useState([]);
  const [invoiceTotal, setinvoiceTotal] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState([]);
  const [deleteInvoiceDialogOpen, setDeleteInvoiceDialogOpen] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState("INV-25-");
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [percentage, setPercentage] = useState("");
  const [showStage, setShowStage] = useState({});
  const { dialBoxopen } = useContext(ProjectContext);
  const [error, setError] = useState(null);
  const { userDetails } = useAuth();
  const userId = userDetails.id;
  const stageRef = useRef(null);
  const [localClaimPercent, setLocalClaimPercent] = useState({});

  // 🔹 Initialize state when invoices data changes
  useEffect(() => {
    if (invoices && invoices.invoices) {
      const initialValues = {};
      invoices.invoices.forEach((invoice) => {
        if (invoice.scopes.length > 0) {
          initialValues[invoice.invoice_id] =
            invoice.scopes.reduce(
              (total, scope) => total + scope.claimpercent,
              0
            ) / invoice.scopes.length;
        } else {
          initialValues[invoice.invoice_id] = 0;
        }
      });
      setLocalClaimPercent(initialValues);
    }
  }, [invoices]);

  // 🔹 Handle updating claim percent on blur
  const handleBlur = (invoiceId, newValue) => {
    setLocalClaimPercent((prev) => ({
      ...prev,
      [invoiceId]: newValue,
    }));

    // 🔥 Send the update to backend
    updateScopeClaimPercentage(invoiceId, newValue);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (stageRef.current && !stageRef.current.contains(event.target)) {
        setShowStage(false);
      }
    };

    // Attach event listener to the document
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    socket.on("connect", () => {
      console.log("Connected to WebSocket server");
    });

    socket.on("invoice added", (newInvoice) => {
      fetchData(
        `/api/phase/invoices/${newInvoice.project_id}/${newInvoice.phase_id}`,
        setInvoices,
        setError
      );
    });

    return () => {
      socket.off("invoice added");
    };
  }, []);
  useEffect(() => {
    if (invoices?.invoices?.length > 0 && invoices.phase_scope_count > 0) {
      const totalClaim = invoices.invoices.reduce(
        (total, invoice) =>
          total +
          invoice.scopes.reduce(
            (scopeTotal, scope) => scopeTotal + scope.claimpercent,
            0
          ),
        0
      );

      // 🔥 Format value to 2 decimal places before storing
      const formattedTotal = (
        totalClaim / invoices.phase_scope_count
      ).toFixed();

      setinvoiceTotal(Number(formattedTotal));
    }
  }, [invoices]);

  const fetchInvoiceData = (phaseId) => {
    fetchData(
      `/api/phase/invoices/${projectId}/${phaseId}`,
      setInvoices,
      setError
    );
    fetchData(`/api/phase/${phaseId}/scope`, setInvoiceScope, setError);
  };
  useEffect(() => {
    if (phaseId) {
      fetchInvoiceData(phaseId);
    }
  }, [phaseId]);
  const handleInvoiceSubmit = async (invoiceDetails, percentage) => {
    try {
      const response = await api.post("/api/invoices", invoiceDetails);
      if (response.status === 201) {
      }
      setPercentage("");
      setInvoiceDate("");
      setInvoiceNo("INV-25-");
      fetchInvoiceData(invoiceDetails.phase_id);
      setSelectedScope([]);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Failed to add invoice.";
      toast.error(`Error: ${errorMessage}`, {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };
  const updateInvoice = async (fieldType, ID, newValue) => {
    try {
      const requestData = { fieldType, newValue };

      const response = await api.put(`/api/invoices/${ID}`, requestData);

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      } else {
        return {
          success: false,
          error: response.statusText || "Unknown error occurred.",
        };
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error(
        error.response?.data?.error || error.message || "Something went wrong.",
        {
          position: "bottom-center",
          autoClose: 2000,
        }
      );
    }
  };

  const updateScopeClaimPercentage = async (invoiceId, claimpercent) => {
    try {
      const response = await api.put(`/api/invoices/scope/${invoiceId}`, {
        claimpercent: Number(claimpercent), // Ensure it's a number
      });

      if (response.status === 200) {
        return { success: true, message: response.data.message };
      } else {
        return {
          success: false,
          error: response.statusText || "Unknown error occurred.",
        };
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error(
        error.response?.data?.error || error.message || "Something went wrong.",
        {
          position: "bottom-center",
          autoClose: 2000,
        }
      );
    }
  };
  const handleDeleteinvoices = (e, invoice_id) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      // Add phaseId to the selectedPhaseId array
      setSelectedInvoiceId((prev) => [...prev, invoice_id]);
    } else {
      // Remove phaseId from the selectedPhaseId array
      setSelectedInvoiceId((prev) => prev.filter((id) => id !== invoice_id));
    }
  };
  const handleDeleteInvoice = async () => {
    if (isCheckboxChecked) {
      try {
        const url = `/api/invoices`; // Backend expects invoiceIds in the request body
        await api.delete(url, { data: { invoiceIds: selectedInvoiceId } }); // Send array of IDs
        // Update state by removing deleted invoices
        setInvoices((prev) => {
          if (!prev || !prev.invoices) return prev; // Ensure state exists
          const updatedInvoices = prev.invoices.filter(
            (inv) => !selectedInvoiceId.includes(inv.id) // Remove deleted invoices
          );
          return {
            ...prev,
            invoices: updatedInvoices, // Correct property name
          };
        });

        // Reset UI states
        setDeleteInvoiceDialogOpen(false);
        setIsCheckboxChecked(false);
        setSelectedInvoiceId([]); // Clear selected invoices
      } catch (error) {
        if (error.response) {
          // Handle server response error
          const serverError = error.response.data;
          console.error("Server error:", serverError);
          setError(
            serverError.message ||
              "An error occurred while deleting invoice. Please try again."
          );
          alert(
            serverError.message ||
              "An error occurred while deleting invoice. Please try again."
          );
        } else {
          // Generic error handling
          console.error("Unexpected error:", error);
          setError("Unexpected error occurred. Please try again.");
        }
      }
    } else {
      toast.error("Please check the checkbox to confirm deletion.", {
        position: "bottom-center",
        autoClose: 2000,
      });
    }
  };

  return (
    <div>
      {showInvoiceTable && invoices && (
        <div className="invoice-table-container">
          <div className="phase-invoice-table-con">
            <div className="invoice-project-details">
              <h3
                style={{ margin: "0 10px 0 0" }}
                className="invoice-details-header"
              >
                Project : {invoices?.projectno}
              </h3>
              <h3
                className="invoice-details-header"
                style={{ margin: "0 10px 0 0" }}
              >
                Phase : {invoices?.phase_no} - {invoices?.phase_name}
              </h3>
              <h3 className="invoice-details-header">
                {" "}
                Claim % : {invoiceTotal}
              </h3>
            </div>
            {selectedInvoiceId.length > 0 && (
              <button
                className="delete-invoice-btn"
                onClick={() => setDeleteInvoiceDialogOpen(true)}
              >
                Delete
              </button>
            )}
            <div className="phase-invoice-table-2">
              <table className="phase-invoice-table">
                <thead>
                  <tr>
                    <th>
                      <FaChevronDown />
                    </th>
                    <th style={{ width: "20px" }}>SNo</th>
                    <th style={{ width: "140px" }}>Invoice No</th>
                    <th>Date</th>
                    <th>Scope</th>
                    <th style={{ width: "25px" }}>%</th>
                    <th style={{ width: "220px" }}>Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices?.invoices && invoices.invoices.length > 0 ? (
                    invoices.invoices.map((invoice, index) => (
                      <tr key={invoice.invoice_id}>
                        <td>
                          <input
                            style={{ cursor: "pointer" }}
                            type="checkbox"
                            name={invoice.invoice_id}
                            checked={selectedInvoiceId.includes(
                              invoice.invoice_id
                            )}
                            onChange={(e) =>
                              handleDeleteinvoices(e, invoice.invoice_id)
                            }
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>{index + 1}</td>
                        <td style={{ padding: "0px" }}>
                          <input
                            className="invoice-no-input"
                            value={invoice.invoiceno || ""}
                            onChange={(e) => {
                              setInvoices((prev) => ({
                                ...prev,
                                invoices: prev.invoices.map((inv) =>
                                  inv.invoice_id === invoice.invoice_id
                                    ? { ...inv, invoiceno: e.target.value }
                                    : inv
                                ),
                              }));
                            }}
                            onBlur={(e) =>
                              updateInvoice(
                                "invoiceno",
                                invoice.invoice_id,
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <DatePicker
                            className="enter-invoice-details-date-table"
                            selected={
                              invoice.date
                                ? typeof invoice.date === "string"
                                  ? parseISO(invoice.date)
                                  : invoice.date
                                : null
                            }
                            onChange={(date) => {
                              updateInvoice("date", invoice.invoice_id, date);
                              setInvoices((prev) => ({
                                ...prev,
                                invoices: prev.invoices.map((inv) =>
                                  inv.invoice_id === invoice.invoice_id
                                    ? { ...inv, date }
                                    : inv
                                ),
                              }));
                            }}
                            dateFormat="dd-MM-yyyy"
                            required
                            popperPlacement="right-middle" // ✅ Opens the date picker on the right
                            zindex="20"
                          />
                        </td>
                        <td>
                          {invoice.scopes && invoice.scopes.length > 0 ? (
                            invoice.scopes.map((scope) => (
                              <div
                                key={scope.scope_id}
                                style={{
                                  backgroundColor: scope.scope_color,
                                }}
                                className="invoice-table-scope"
                              >
                                {scope.scope_name}
                              </div>
                            ))
                          ) : (
                            <span></span>
                          )}
                        </td>

                        {/* 🔥 Aggregate Total Claim Percent for the Invoice */}
                        <td style={{ padding: "5px" }}>
                          <input
                            type="number"
                            className="invoice-percentage-input-dial"
                            value={localClaimPercent[invoice.invoice_id] || 0}
                            onChange={(e) =>
                              setLocalClaimPercent((prev) => ({
                                ...prev,
                                [invoice.invoice_id]: e.target.value,
                              }))
                            }
                            onBlur={(e) =>
                              handleBlur(invoice.invoice_id, e.target.value)
                            }
                          />
                        </td>

                        <td
                          style={{ cursor: "pointer" }}
                          title={
                            invoice.updated_at
                              ? format(invoice.updated_at, "dd-MM-yyyy HH:mm")
                              : ""
                          }
                        >
                          {invoice.invoiced_by_name}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        No invoices available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div style={{ marginTop: "10px", fontWeight: "bold" }}></div>
            </div>
            <form
              className="phase-invoice-footer"
              onSubmit={(e) => {
                e.preventDefault();
                const invoiceDetails = {
                  invoicno: invoiceNo,
                  date: invoiceDate,
                  invoiced_by: userId,
                  project_id: projectId,
                  phase_id: phaseId,
                  claimpercent: Number(percentage),
                  scope_id: selectedScope,
                };
                handleInvoiceSubmit(invoiceDetails, percentage);
              }}
            >
              <div>
                <label style={{ marginLeft: "10px", fontSize: "14px" }}>
                  INV No:
                  <input
                    className="enter-invoice-details-input"
                    type="text"
                    required
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                  />
                </label>
                <label
                  style={{
                    marginLeft: "10px",
                    position: "fixed",
                    fontSize: "14px",
                  }}
                >
                  Date:
                  <DatePicker
                    className="enter-invoice-details-date"
                    selected={invoiceDate}
                    onChange={(date) => setInvoiceDate(date)}
                    dateFormat="dd-MM-yyyy"
                    required
                  />
                </label>
                <label className="invoice-scope-input">Scope:</label>
                <div>
                  <InvoiceScope
                    invoiceScope={invoiceScope}
                    selectedScope={selectedScope}
                    setSelectedScope={setSelectedScope}
                  />
                </div>

                <label
                  className="invoice-percentage-input"
                  style={{ fontSize: "14px" }}
                >
                  % :
                  <input
                    className="enter-invoice-details-input"
                    style={{ marginLeft: "8px", width: "40px" }}
                    type="text"
                    required
                    value={percentage}
                    onChange={(e) => setPercentage(e.target.value)}
                  />
                </label>
              </div>

              <button
                className="add-btn invoice-details-button"
                type="submit"
                style={{
                  backgroundColor: invoiceTotal === 100 ? "grey" : "#6200ee",
                }}
                disabled={invoiceTotal === 100}
              >
                Add
              </button>
              <button
                className="cancel-btn invoice-details-can-button"
                onClick={() => {
                  setShowInvoiceTable(false);
                  setPercentage("");
                  setInvoiceDate("");
                  setInvoiceNo("INV-25-");
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
      {deleteInvoiceDialogOpen && (
        <div className={`delete-phase-dial ${dialBoxopen ? "open" : "close"} `}>
          <div className="delete-dial">
            <div className="dial-d-header">
              <h3
                style={{ backgroundColor: "transparent", marginLeft: "15px" }}
              >
                Delete Invoice
              </h3>
            </div>
            <div style={{ backgroundColor: "white" }}>
              <p style={{ backgroundColor: "white" }}>
                Are you sure you want to delete this Invoice ? This action
                cannot be undone.
              </p>
              <input
                style={{ cursor: "pointer" }}
                type="checkbox"
                id="delete-phase"
                checked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked((prev) => !prev)}
              />
              <label
                htmlFor="delete-phase"
                style={{ backgroundColor: "white", cursor: "pointer" }}
              >
                I understand this action cannot be undone.
              </label>
            </div>
            <div className="delete-dial-btn-con">
              <button
                onClick={handleDeleteInvoice}
                color="secondary"
                variant="contained"
                disabled={!isCheckboxChecked}
                className="add-btn"
                style={{ backgroundColor: isCheckboxChecked ? "red" : "gray" }}
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteInvoiceDialogOpen(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
